import React, { useEffect, useMemo, useState } from "react";
import { Button } from "reactstrap";
import { toast } from "react-toastify";

function usePagination(items, pageSize, resetKey) {
	const [page, setPage] = useState(0);
	useEffect(() => {
		setPage(0);
	}, [resetKey, items.length]);

	const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
	const safePage = Math.min(page, pageCount - 1);
	const slice = items.slice(safePage * pageSize, safePage * pageSize + pageSize);

	return {
		items: slice,
		page: safePage,
		pageCount,
		total: items.length,
		canPrev: safePage > 0,
		canNext: safePage < pageCount - 1,
		prev: () => setPage((p) => Math.max(0, p - 1)),
		next: () => setPage((p) => Math.min(pageCount - 1, p + 1)),
		goTo: (p) => setPage(Math.max(0, Math.min(pageCount - 1, p))),
	};
}

function buildPageItems(pageCount, currentPage) {
	if (pageCount <= 5) {
		return Array.from({ length: pageCount }, (_, i) => ({ type: "page", page: i }));
	}

	const items = [];
	const seen = new Set();
	const addPage = (p) => {
		if (p < 0 || p >= pageCount || seen.has(p)) return;
		seen.add(p);
		items.push({ type: "page", page: p });
	};
	const addEllipsis = (key) => items.push({ type: "ellipsis", key });

	addPage(0);
	if (currentPage > 2) addEllipsis("start");

	for (
		let p = Math.max(1, currentPage - 1);
		p <= Math.min(pageCount - 2, currentPage + 1);
		p += 1
	) {
		addPage(p);
	}

	if (currentPage < pageCount - 3) addEllipsis("end");
	addPage(pageCount - 1);

	return items;
}

export default function TrafficListDialog({
	title,
	detail,
	rows,
	columns,
	rowKey,
	searchText,
	copyText,
	unit = "row",
	unitPlural,
	pageSize = 10,
	onClose,
}) {
	const [q, setQ] = useState("");
	const [copied, setCopied] = useState(false);
	const needle = q.trim().toLowerCase();
	const filtered = useMemo(() => {
		if (!needle) return rows || [];
		return (rows || []).filter((r) =>
			searchText(r).toLowerCase().includes(needle)
		);
	}, [rows, needle, searchText]);

	const pager = usePagination(filtered, pageSize, needle);
	const plural = unitPlural || `${unit}s`;
	const rangeStart = pager.total === 0 ? 0 : pager.page * pageSize + 1;
	const rangeEnd = Math.min((pager.page + 1) * pageSize, pager.total);
	const pageItems = buildPageItems(pager.pageCount, pager.page);

	useEffect(() => {
		function onKey(e) {
			if (e.key === "Escape") onClose();
		}
		window.addEventListener("keydown", onKey);
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			window.removeEventListener("keydown", onKey);
			document.body.style.overflow = prev;
		};
	}, [onClose]);

	useEffect(() => {
		setCopied(false);
	}, [needle, rows]);

	const copyAll = async () => {
		if (!copyText || !filtered.length) return;
		try {
			await navigator.clipboard.writeText(filtered.map(copyText).join("\n"));
			setCopied(true);
			const count = filtered.length;
			const label = count === 1 ? unit : plural;
			toast.success(`Copied ${count} ${label} to clipboard`);
			window.setTimeout(() => setCopied(false), 2000);
		} catch {
			toast.error("Could not copy list");
		}
	};

	return (
		<div
			className="monitoring__ai-traffic-list-dialog"
			role="dialog"
			aria-modal="true"
			onClick={onClose}
		>
			<div
				className="monitoring__ai-traffic-list-dialog-content"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="monitoring__ai-traffic-list-dialog-header">
					<div>
						<h3 className="monitoring__ai-traffic-list-dialog-title">{title}</h3>
						{detail && (
							<p className="monitoring__ai-traffic-list-dialog-detail mb-0">
								{detail}
							</p>
						)}
					</div>
					<button
						type="button"
						className="monitoring__ai-traffic-list-dialog-close"
						onClick={onClose}
					>
						Close
					</button>
				</div>

				<div className="monitoring__ai-traffic-list-dialog-toolbar">
					<input
						autoFocus
						value={q}
						onChange={(e) => setQ(e.target.value)}
						placeholder="Search…"
						aria-label="Search list"
						className="monitoring__ai-traffic-list-search"
					/>
					{needle && (
						<span className="monitoring__ai-traffic-list-match-count">
							matching {filtered.length} of {rows?.length ?? 0}
						</span>
					)}
					{copyText && filtered.length > 0 && (
						<Button
							size="sm"
							color="primary"
							className={`monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-list-copy-btn${
								copied ? " monitoring__ai-traffic-list-copy-btn--copied" : ""
							}`}
							onClick={copyAll}
							aria-live="polite"
						>
							<i
								className={copied ? "fas fa-check" : "far fa-copy"}
								aria-hidden
							/>
							{copied ? "Copied" : "Copy list"}
						</Button>
					)}
				</div>

				<div className="monitoring__ai-traffic-list-dialog-body">
					{filtered.length === 0 ? (
						<p className="monitoring__ai-traffic-list-empty mb-0">
							{needle ? `No ${unit} matches "${q}".` : `No ${plural} to show.`}
						</p>
					) : (
						<div className="monitoring__ai-traffic-list-table-wrap">
							<table className="monitoring__ai-traffic-list-table">
								<thead>
									<tr>
										{columns.map((c) => (
											<th
												key={c.key}
												scope="col"
												className={
													c.align === "right"
														? "monitoring__ai-traffic-list-th monitoring__ai-traffic-list-th--num"
														: "monitoring__ai-traffic-list-th"
												}
											>
												{c.label}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{pager.items.map((row) => (
										<tr key={rowKey(row)}>
											{columns.map((c) => (
												<td
													key={c.key}
													className={[
														"monitoring__ai-traffic-list-td",
														c.align === "right"
															? "monitoring__ai-traffic-list-td--num"
															: "",
														c.key === "url" ? "monitoring__ai-traffic-list-td--page" : "",
														c.cellClass || "",
													]
														.filter(Boolean)
														.join(" ")}
												>
													{c.render(row)}
												</td>
											))}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>

				{filtered.length > 0 && (
					<div className="monitoring__ai-traffic-list-dialog-footer">
						<span className="monitoring__ai-traffic-list-range">
							{rangeStart}-{rangeEnd} of {pager.total} {plural}
						</span>
						<div className="monitoring__ai-traffic-list-pager">
							<Button
								size="sm"
								color="primary"
								className="monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-list-pager-btn"
								disabled={!pager.canPrev}
								onClick={pager.prev}
							>
								Prev
							</Button>
							<div
								className="monitoring__ai-traffic-list-page-nums"
								role="group"
								aria-label="Pages"
							>
								{pageItems.map((item) =>
									item.type === "ellipsis" ? (
										<span
											key={item.key}
											className="monitoring__ai-traffic-list-page-ellipsis"
										>
											…
										</span>
									) : (
										<button
											key={item.page}
											type="button"
											className={`monitoring__ai-traffic-list-page-num${
												item.page === pager.page
													? " monitoring__ai-traffic-list-page-num--active"
													: ""
											}`}
											aria-current={item.page === pager.page ? "page" : undefined}
											onClick={() => pager.goTo(item.page)}
										>
											{item.page + 1}
										</button>
									)
								)}
							</div>
							<Button
								size="sm"
								color="primary"
								className="monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-list-pager-btn"
								disabled={!pager.canNext}
								onClick={pager.next}
							>
								Next
							</Button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
